/* eslint-disable react/prop-types */
import React from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import AuthImg from "@/components/AuthImg";
import {
  User2, Mail, Phone, Smartphone, Building2, MapPin, CalendarClock, Clock,
  CreditCard, ShieldCheck, Banknote, Sparkles, Copy, Zap,
} from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  admin: "Admin", gutachter: "Gutachter", backoffice: "Backoffice",
  mitarbeiter: "Mitarbeiter", franchise: "Franchise", provision: "Provisionspartner",
};
const STATUS_LABELS = {
  active: "Aktiv", inactive: "Inaktiv", suspended: "Gesperrt", deactivated: "Deaktiviert",
};
const SUB_LABELS = {
  active: "Aktiv", trialing: "Testphase", trial_expired: "Trial abgelaufen",
  past_due: "Zahlung offen", cancelled: "Gekündigt", canceled: "Gekündigt",
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }) + " Uhr";
  } catch { return iso; }
};

function Row({ icon: Icon, label, value, testid, copyable }) {
  const val = value === undefined || value === null || value === "" ? "—" : value;
  const copy = () => {
    navigator.clipboard?.writeText(String(val));
    toast.success(`${label} kopiert`);
  };
  return (
    <div className="flex items-start gap-2.5 py-1.5" data-testid={testid}>
      {Icon && <Icon className="h-3.5 w-3.5 text-zinc-400 mt-0.5 shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-zinc-400">{label}</div>
        <div className="text-sm text-zinc-900 break-words flex items-center gap-1.5">
          <span className="min-w-0">{val}</span>
          {copyable && val !== "—" && (
            <button type="button" onClick={copy} className="text-zinc-300 hover:text-zinc-600 transition-colors shrink-0" title="Kopieren">
              <Copy className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-zinc-50/50 px-4 py-3">
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-1">{title}</div>
      <div className="divide-y divide-zinc-100">{children}</div>
    </div>
  );
}

export function UserDetailDialog({ user, presence, isNew, onOpenChange }) {
  if (!user) return null;
  const p = presence || {};
  const isProvision = user.role === "provision" || user.work_type === "provision";
  const address = [user.address_line, [user.postal_code, user.city].filter(Boolean).join(" ")]
    .filter(Boolean).join(", ");
  const billingMode = user.stripe_subscription_id
    ? "Stripe-Voll-Automatik"
    : (user.billing_mode === "stripe_auto" ? "Stripe-Voll-Automatik" : "Software-Einzug (Standard)");

  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onOpenChange(false); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="user-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="relative h-11 w-11 rounded-full overflow-hidden bg-zinc-100 flex items-center justify-center text-sm font-semibold text-zinc-600 shrink-0">
              {user.avatar_path ? (
                <AuthImg path={user.avatar_path} className="h-full w-full object-cover" alt={user.name} />
              ) : (
                user.name?.slice(0, 2).toUpperCase() || <User2 className="h-5 w-5" />
              )}
            </div>
            <span className="flex items-center gap-2 min-w-0">
              <span className="truncate" data-testid="user-detail-name">{user.name || user.email}</span>
              {isNew && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-sky-600 text-white px-1.5 py-0.5 rounded shrink-0">
                  <Sparkles className="h-2.5 w-2.5" /> Neu
                </span>
              )}
            </span>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="bg-zinc-50">{ROLE_LABELS[user.role] || user.role}</Badge>
            <Badge variant="outline" className={isProvision ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-cyan-50 text-cyan-700 border-cyan-200"}>
              {isProvision ? "Provisionsbasis · 200 €/Auftrag" : "Selbstständig"}
            </Badge>
            <Badge variant="outline" className={user.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}>
              {STATUS_LABELS[user.status] || user.status}
            </Badge>
            {p.is_online && (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-semibold">
                <span className="h-2 w-2 rounded-full bg-emerald-500" /> Online
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3">
          <Section title="Registrierung">
            <Row icon={CalendarClock} label="Registriert am" value={fmtDateTime(user.created_at)} testid="user-detail-created" />
            {user.trial_started_at && <Row icon={Clock} label="Testphase gestartet" value={fmtDateTime(user.trial_started_at)} />}
            <Row icon={Clock} label="Zuletzt online" value={fmtDateTime(p.last_seen_at || user.last_seen_at)} testid="user-detail-lastseen" />
          </Section>

          <Section title="Kontakt">
            <Row icon={Mail} label="E-Mail" value={user.email} testid="user-detail-email" copyable />
            <Row icon={Phone} label="Telefon" value={user.phone} copyable />
            <Row icon={Smartphone} label="Mobil" value={user.mobile} copyable />
            <Row icon={Building2} label="Firma" value={user.company} testid="user-detail-company" />
          </Section>

          <Section title="Adresse">
            <Row icon={MapPin} label="Anschrift" value={address} testid="user-detail-address" copyable />
          </Section>

          <Section title="Abo & Zahlung">
            {isProvision ? (
              <Row icon={Banknote} label="Vergütung" value="200 € pro eingereichtem Auftrag (kein Abo)" />
            ) : (
              <>
                <Row icon={CreditCard} label="Paket" value={user.subscription_plan ? user.subscription_plan.charAt(0).toUpperCase() + user.subscription_plan.slice(1) : "—"} testid="user-detail-plan" />
                <Row icon={CreditCard} label="Abo-Status"
                  value={
                    (SUB_LABELS[p.subscription_status || user.subscription_status] || "—")
                    + (user.cancellation_effective_at ? ` · gekündigt zum ${fmtDateTime(user.cancellation_effective_at).slice(0, 10)}` : "")
                  }
                  testid="user-detail-substatus" />
                <Row icon={Zap} label="Abrechnungsart" value={billingMode} />
                <Row icon={ShieldCheck} label="SEPA bei Stripe"
                  value={user.stripe_sepa_pm_id ? "✓ Mandat hinterlegt — Einzug möglich" : "✗ noch kein Stripe-Mandat"} />
              </>
            )}
          </Section>

          {(user.vat_id || user.tax_number || user.iban || user.bank_name) && (
            <Section title="Steuer & Bank">
              <Row icon={Banknote} label="USt-IdNr." value={user.vat_id} copyable />
              <Row icon={Banknote} label="Steuernummer" value={user.tax_number} copyable />
              <Row icon={Banknote} label="IBAN" value={user.iban} copyable />
              <Row icon={Banknote} label="Bank" value={user.bank_name} />
            </Section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
